
import React, { Suspense, lazy, useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, useLocation, useParams, matchPath } from 'react-router-dom';
import './App.css';
import Navbar from './Pages/Navbar';
import LandingPage from './Pages/LandingPage';
import Appointment from './Pages/Appointment';
import ChatApp from './Pages/ChatApp';
import Profile from './Pages/Profile';
import Dashboard from './Pages/PagesData/Admin/Dashboard';
import Topbar from './Pages/Topbar';
import WhatsAppFab from './Components/WhatsAppFab';
import ApBooking from './Components/ApBooking';
import BookAppointmentModal from './Components/BookAppointmentModal';
import Blog from './Pages/Blog';
import BlogPost from './Pages/BlogPost';
import Seo from './Components/Seo';
import { getBlogPostBySlug } from './Pages/PagesData/BlogPosts';
import AdminBlog from './Pages/AdminBlog';
import ForgotPassword from './Pages/ForgotPassword';
import ResetPassword from './Pages/ResetPassword';
import { useLocale } from './context/LocaleContext';
import { paths } from './routes/paths';
import {
  getOrthoMenuPageByPath,
  ORTHO_MENU_PAGES,
  orthoPagePath,
} from './data/orthoMenuPages';
import OrthoMenuPage from './Pages/OrthoMenuPage';
import LocationPage from './Pages/LocationPage';
import LandingEmergencyCusco from './Pages/LandingEmergencyCusco';
import LibroReclamaciones from './Pages/LibroReclamaciones';
import { useBookAppointmentModal } from './context/BookAppointmentModalContext';
import {
  emergencySeo,
  buildEmergencyJsonLd,
} from './data/emergencyDentistCusco';

const Login = lazy(() => import('./Pages/Login'));
const Register = lazy(() => import('./Pages/Register'));

function RedirectBlogPost() {
  const { slug } = useParams();
  return <Navigate to={paths.blogPost(slug)} replace />;
}

function RedirectAdminBlogEdit() {
  const { slug } = useParams();
  return <Navigate to={paths.adminBlogEdit(slug)} replace />;
}

function BookingModalRedirect() {
  const { openBookAppointmentModal } = useBookAppointmentModal();

  useEffect(() => {
    openBookAppointmentModal();
  }, [openBookAppointmentModal]);

  return <Navigate to="/" replace />;
}

const App = () => {
  const location = useLocation();
  const { locale, t } = useLocale();

  const seo = useMemo(() => {
    const path = location.pathname || '/';

    if (matchPath('/blog/:slug', path)) {
      const slug = path.split('/').pop();
      const post = getBlogPostBySlug(slug);
      if (post) {
        const title = post.seoTitle || post.title;
        const description = post.seoDescription || post.excerpt;
        return {
          title,
          description,
          pathname: path,
          lang: post.locale === 'en' ? 'en' : 'es-PE',
          jsonLd: {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: post.title,
            datePublished: post.date,
            description: post.excerpt,
            inLanguage: post.locale === 'en' ? 'en' : 'es-PE',
            mainEntityOfPage: { '@type': 'WebPage', '@id': path },
          },
        };
      }
    }

    if (path === paths.blog) {
      return {
        title: t('seo.blogTitle'),
        description: t('seo.blogDescription'),
        pathname: path,
        lang: locale === 'en' ? 'en' : 'es-PE',
      };
    }

    if (path === paths.reservas) {
      return {
        title: t('seo.reservasTitle'),
        description: t('seo.reservasDescription'),
        pathname: path,
        lang: locale === 'en' ? 'en' : 'es-PE',
      };
    }

    if (path === paths.emergency) {
      return {
        title: emergencySeo.title,
        description: emergencySeo.description,
        pathname: path,
        lang: 'en',
        imagePathname: '/og-emergency-dentist-cusco.png',
        jsonLd: buildEmergencyJsonLd(path),
      };
    }

    if (path === paths.libroReclamaciones) {
      return {
        title: t('seo.libroTitle'),
        description: t('seo.libroDescription'),
        pathname: path,
        lang: locale === 'en' ? 'en' : 'es-PE',
      };
    }

    if (path === paths.appointment) {
      return {
        title: t('seo.appointmentTitle'),
        description: t('seo.appointmentDescription'),
        pathname: path,
        lang: locale === 'en' ? 'en' : 'es-PE',
      };
    }

    const orthoPage = getOrthoMenuPageByPath(path);
    if (orthoPage) {
      const base = `orthoPages.pages.${orthoPage.id}`;
      const title = t(`${base}.seoTitle`);
      const description = t(`${base}.seoDescription`);
      return {
        title,
        description,
        pathname: path,
        lang: locale === 'en' ? 'en' : 'es-PE',
        jsonLd: {
          '@context': 'https://schema.org',
          '@type': 'MedicalWebPage',
          name: t(`${base}.h1`),
          description,
          inLanguage: locale === 'en' ? 'en' : 'es-PE',
          about: {
            '@type': 'MedicalProcedure',
            name: t(`${base}.h1`),
            medicalSpecialty: 'Orthodontics',
          },
          provider: {
            '@type': 'Dentist',
            name: 'Smile Up',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Av. La Cultura 822',
              addressLocality: 'Cusco',
              addressCountry: 'PE',
            },
          },
          mainEntityOfPage: { '@type': 'WebPage', '@id': path },
        },
      };
    }

    return {
      title: t('seo.homeTitle'),
      description: t('seo.homeDescription'),
      pathname: path,
      lang: locale === 'en' ? 'en' : 'es-PE',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Dentist',
        name: 'Smile Up',
        medicalSpecialty: 'Orthodontics',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Cusco',
          addressCountry: 'PE',
        },
        areaServed: ['Cusco', 'Peru'],
        availableLanguage: ['es', 'en'],
        url: '/',
      },
    };
  }, [location.pathname, locale, t]);

  const isAuthLandingPage =
    location.pathname === paths.login ||
    location.pathname === paths.register ||
    location.pathname === '/forgot-password' ||
    location.pathname === '/reset-password';

  return (
    <>
      <Seo {...seo} />

      {!isAuthLandingPage ? <Topbar /> : null}
      {!isAuthLandingPage ? <Navbar /> : null}
      {!isAuthLandingPage ? <WhatsAppFab /> : null}
      {!isAuthLandingPage ? <ApBooking /> : null}
      {!isAuthLandingPage ? <BookAppointmentModal /> : null}

      <Routes>
        <Route exact path="/" element={<LandingPage />} />
        <Route
          path="/inicio"
          element={<Navigate to={{ pathname: '/', hash: '#home' }} replace />}
        />
        <Route
          path="/nosotros"
          element={<Navigate to={orthoPagePath('drProfile')} replace />}
        />
        <Route
          path="/servicios"
          element={<Navigate to={{ pathname: '/', hash: '#our-services' }} replace />}
        />
        <Route
          path="/ubicacion"
          element={<Navigate to={paths.location} replace />}
        />
        <Route exact path={paths.appointment} element={<Appointment />} />
        <Route exact path={paths.team} element={<Profile />} />
        <Route
          exact
          path={paths.register}
          element={
            <Suspense fallback={<div className="route-loading" aria-busy="true" />}>
              <Register />
            </Suspense>
          }
        />
        <Route
          exact
          path={paths.login}
          element={
            <Suspense fallback={<div className="route-loading" aria-busy="true" />}>
              <Login />
            </Suspense>
          }
        />
        <Route exact path="/forgot-password" element={<ForgotPassword />} />
        <Route exact path="/reset-password" element={<ResetPassword />} />
        <Route
          path={paths.userProfile}
          element={<Navigate to="/" replace />}
        />
        <Route exact path={paths.userChat} element={<ChatApp />} />
        <Route exact path={paths.reservas} element={<BookingModalRedirect />} />
        <Route exact path={paths.blog} element={<Blog />} />
        <Route exact path="/blog/:slug" element={<BlogPost />} />
        <Route exact path={paths.emergency} element={<LandingEmergencyCusco />} />
        <Route exact path={paths.libroReclamaciones} element={<LibroReclamaciones />} />
        <Route
          path="/dental-emergency-cusco"
          element={<Navigate to={paths.emergency} replace />}
        />
        <Route
          path="/emergency-dentist-cusco-peru"
          element={<Navigate to={paths.emergency} replace />}
        />
        <Route
          exact
          path={paths.implants}
          element={<Navigate to={{ pathname: '/', hash: '#our-services' }} replace />}
        />
        <Route exact path={paths.adminPerson} element={<Dashboard />} />
        <Route path="/admin/blog/edit/:slug" element={<AdminBlog />} />
        <Route exact path={paths.adminBlog} element={<AdminBlog />} />

        <Route exact path={paths.location} element={<LocationPage />} />

        <Route
          path="/dr-paniura/perfil-ortodoncista-cusco"
          element={<Navigate to={orthoPagePath('drProfile')} replace />}
        />
        <Route
          path="/por-que/ortodoncia-cusco-smile-up"
          element={<Navigate to={orthoPagePath('whySmileUp')} replace />}
        />
        <Route
          path="/ortodoncia/ortopedia-maxilar-cusco"
          element={<Navigate to={orthoPagePath('klamm')} replace />}
        />

        {ORTHO_MENU_PAGES.filter((p) => p.id !== 'location').map((p) => (
          <Route
            key={p.path}
            path={p.path}
            element={<OrthoMenuPage pageId={p.id} />}
          />
        ))}

        {/* Redirecciones desde URLs antiguas (/dental-clinic/...) */}
        <Route path="/dental-clinic/nosotros" element={<Navigate to="/nosotros" replace />} />
        <Route path="/dental-clinic/servicios" element={<Navigate to="/servicios" replace />} />
        <Route path="/dental-clinic/ubicacion" element={<Navigate to={paths.location} replace />} />
        <Route path="/dental-clinic/appointment" element={<Navigate to={paths.appointment} replace />} />
        <Route path="/dental-clinic/team" element={<Navigate to={paths.team} replace />} />
        <Route path="/dental-clinic/user/profile" element={<Navigate to="/" replace />} />
        <Route path="/dental-clinic/user/chat_section" element={<Navigate to={paths.userChat} replace />} />
        <Route path="/dental-clinic/reservas" element={<Navigate to={paths.reservas} replace />} />
        <Route path="/dental-clinic/blog" element={<Navigate to={paths.blog} replace />} />
        <Route path="/dental-clinic/blog/:slug" element={<RedirectBlogPost />} />
        <Route path="/dental-clinic/emergency-dentist-cusco" element={<Navigate to={paths.emergency} replace />} />
        <Route path="/dental-clinic/dental-emergency-cusco" element={<Navigate to={paths.emergency} replace />} />
        <Route path="/dental-clinic/dental-implants-cusco" element={<Navigate to={{ pathname: '/', hash: '#our-services' }} replace />} />
        <Route path="/dental-clinic/admin-person" element={<Navigate to={paths.adminPerson} replace />} />
        <Route path="/dental-clinic/admin/blog" element={<Navigate to={paths.adminBlog} replace />} />
        <Route path="/dental-clinic/admin/blog/edit/:slug" element={<RedirectAdminBlogEdit />} />
      </Routes>
    </>
  );
};

export default App;
